/**
 * Pure three-card poker: rank a three-card hand, compare two of them, decide
 * whether the dealer qualifies, and settle ante, play, ante bonus and Pair
 * Plus. No room, no sockets, no shoe: the server layer supplies the cards and
 * owns the sequencing.
 *
 * The rankings are the game's own, not the five-card ones next door in
 * `poker.ts`: with three cards a straight beats a flush, three of a kind
 * beats a straight, and the ace plays low in exactly one place (A-2-3).
 */
import type { ThreecardRules } from './threecardRules.js';
import type { Card } from './types.js';
import { pokerRank } from './poker.js';

/** Weakest first; the index doubles as the category's strength. */
export type ThreecardCategory =
  | 'high_card'
  | 'pair'
  | 'flush'
  | 'straight'
  | 'trips'
  | 'straight_flush';

export const THREECARD_CATEGORIES: readonly ThreecardCategory[] = [
  'high_card',
  'pair',
  'flush',
  'straight',
  'trips',
  'straight_flush',
];

const categoryIndex = (category: ThreecardCategory): number =>
  THREECARD_CATEGORIES.indexOf(category);

/**
 * A scored three-card hand. The score is lexicographic — category index
 * first, then the ranks that break ties inside the category — so two hands
 * compare by walking one array, exactly as the five-card ranks do.
 */
export interface ThreecardRank {
  category: ThreecardCategory;
  score: number[];
  /** Table-talk name, e.g. "Queen-high straight". */
  label: string;
}

const SINGULAR: Record<number, string> = {
  2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight',
  9: 'nine', 10: 'ten', 11: 'jack', 12: 'queen', 13: 'king', 14: 'ace',
};

const PLURAL: Record<number, string> = {
  2: 'twos', 3: 'threes', 4: 'fours', 5: 'fives', 6: 'sixes', 7: 'sevens',
  8: 'eights', 9: 'nines', 10: 'tens', 11: 'jacks', 12: 'queens', 13: 'kings',
  14: 'aces',
};

const rankName = (value: number): string => SINGULAR[value] ?? String(value);
const rankPlural = (value: number): string => PLURAL[value] ?? `${value}s`;

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * The straight's high card, or null. Returns 3 for the wheel (A-2-3), so the
 * ace plays low exactly once and never beats a four-high straight.
 */
function straightHigh(values: readonly number[]): number | null {
  const unique = [...new Set(values)].sort((a, b) => b - a);
  if (unique.length !== 3) return null;
  if (unique[0]! - unique[2]! === 2) return unique[0]!;
  if (unique[0] === 14 && unique[1] === 3 && unique[2] === 2) return 3;
  return null;
}

/** Score exactly three cards. Throws on any other count — callers deal three. */
export function rankThree(cards: readonly Card[]): ThreecardRank {
  if (cards.length !== 3) throw new Error(`rankThree needs 3 cards, got ${cards.length}`);
  const values = cards.map((c) => pokerRank(c.rank)).sort((a, b) => b - a);
  const flush = new Set(cards.map((c) => c.suit)).size === 1;
  const high = straightHigh(values);

  if (high !== null) {
    if (flush) {
      return {
        category: 'straight_flush',
        score: [categoryIndex('straight_flush'), high],
        label: high === 14 ? 'Mini royal' : `${capitalize(rankName(high))}-high straight flush`,
      };
    }
    return {
      category: 'straight',
      score: [categoryIndex('straight'), high],
      label: `${capitalize(rankName(high))}-high straight`,
    };
  }

  const [a, b, c] = values as [number, number, number];
  if (a === b && b === c) {
    return {
      category: 'trips',
      score: [categoryIndex('trips'), a],
      label: `Three of a kind, ${rankPlural(a)}`,
    };
  }
  if (flush) {
    return {
      category: 'flush',
      score: [categoryIndex('flush'), ...values],
      label: `${capitalize(rankName(a))}-high flush`,
    };
  }
  if (a === b || b === c) {
    const pair = b;
    const kicker = a === b ? c : a;
    return {
      category: 'pair',
      score: [categoryIndex('pair'), pair, kicker],
      label: `Pair of ${rankPlural(pair)}`,
    };
  }
  return {
    category: 'high_card',
    score: [categoryIndex('high_card'), ...values],
    label: `${capitalize(rankName(a))} high`,
  };
}

/** Positive when `a` wins, negative when `b` does, 0 on a genuine tie. */
export function compareThreecardRanks(a: ThreecardRank, b: ThreecardRank): number {
  const length = Math.max(a.score.length, b.score.length);
  for (let i = 0; i < length; i += 1) {
    const diff = (a.score[i] ?? 0) - (b.score[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * The dealer plays only with queen-high or better. Any made hand qualifies —
 * a pair of deuces outranks an ace high — so the check is the category or
 * the high card.
 */
export function dealerQualifies(rank: ThreecardRank): boolean {
  return rank.category !== 'high_card' || rank.score[1]! >= 12;
}

/**
 * Ante bonus multipliers, paid on the ante no matter how the play bet fares —
 * even against a qualifying dealer who beats you. The classic table:
 * straight 1:1, three of a kind 4:1, straight flush 5:1 (mini royal included).
 */
export function anteBonusMultiplier(rank: ThreecardRank): number {
  switch (rank.category) {
    case 'straight_flush':
      return 5;
    case 'trips':
      return 4;
    case 'straight':
      return 1;
    default:
      return 0;
  }
}

/** The Pair Plus paytable, as ":1" odds on the stake. Classic 1-4-6-30-40. */
export const PAIR_PLUS_PAYTABLE: Record<Exclude<ThreecardCategory, 'high_card'>, number> = {
  pair: 1,
  flush: 4,
  straight: 6,
  trips: 30,
  straight_flush: 40,
};

/** Chips returned on a Pair Plus bet, stake included; 0 on queen-high-or-worse. */
export function settlePairPlus(stake: number, rank: ThreecardRank): number {
  if (rank.category === 'high_card') return 0;
  return stake * (1 + PAIR_PLUS_PAYTABLE[rank.category]);
}

/** What one ante-and-play pair came back, before Pair Plus. */
export interface ThreecardSettlement {
  /** Chips returned on the ante, stake included. */
  antePayout: number;
  /** Chips returned on the play bet, stake included. */
  playPayout: number;
  /** Chips returned by the ante bonus. */
  bonusPayout: number;
  /** True when the dealer's hand reached queen-high. */
  qualifies: boolean;
  /** How the play bet fared: null when the dealer never qualified. */
  outcome: 'win' | 'loss' | 'push' | null;
}

/**
 * Settle a played hand against the dealer. A non-qualifying dealer pays the
 * ante even money and pushes the play bet; a qualifying one pays both or
 * takes both, with a tie pushing.
 */
export function settleThreecard(
  ante: number,
  play: number,
  playerRank: ThreecardRank,
  dealerRank: ThreecardRank,
  rules: Pick<ThreecardRules, 'anteBonus'>,
): ThreecardSettlement {
  const qualifies = dealerQualifies(dealerRank);
  let antePayout = 0;
  let playPayout = 0;
  let outcome: ThreecardSettlement['outcome'] = null;

  if (!qualifies) {
    antePayout = ante * 2;
    playPayout = play;
  } else {
    const cmp = compareThreecardRanks(playerRank, dealerRank);
    if (cmp > 0) {
      antePayout = ante * 2;
      playPayout = play * 2;
      outcome = 'win';
    } else if (cmp === 0) {
      antePayout = ante;
      playPayout = play;
      outcome = 'push';
    } else {
      outcome = 'loss';
    }
  }

  return {
    antePayout,
    playPayout,
    bonusPayout: rules.anteBonus ? ante * anteBonusMultiplier(playerRank) : 0,
    qualifies,
    outcome,
  };
}

/** What one settled hand wrote into the TV's history strip. */
export type ThreecardOutcome =
  | 'ante' // dealer did not qualify: antes paid, play bets pushed
  | 'play' // a played hand beat a qualifying dealer
  | 'dealer' // the dealer's hand held the felt
  | 'push' // a qualifying tie
  | 'fold'; // everyone mucked; the dealer never showed

/** Which hands were out there, for the results column. */
export const THREECARD_OUTCOMES: readonly ThreecardOutcome[] = [
  'ante',
  'play',
  'dealer',
  'push',
  'fold',
];

/** The one side bet three-card poker carries, staked beside the ante. */
export type ThreecardSideBetId = 'pair_plus';

export const VALID_THREECARD_SIDE_BETS: readonly ThreecardSideBetId[] = ['pair_plus'];
